/* Day/night mode store.
 *
 * Persisted to sessionStorage (per the spec) — the toggle survives navigation
 * and refresh within a session, but a fresh tab opens in day mode. This
 * matches the editorial intent: night mode is a deliberate choice for the
 * current reading session, not a permanent preference.
 */

import { create } from 'zustand';

export type Mode = 'day' | 'night';

const STORAGE_KEY = 'networker.theme.v1';

function readInitial(): Mode {
  if (typeof window === 'undefined') return 'day';
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored === 'day' || stored === 'night') return stored;
  return 'day';
}

interface ThemeStore {
  mode: Mode;
  setMode: (m: Mode) => void;
  toggle: () => void;
}

export const useTheme = create<ThemeStore>((set, get) => ({
  mode: readInitial(),
  setMode: (m) => {
    sessionStorage.setItem(STORAGE_KEY, m);
    document.body.classList.toggle('dark', m === 'night');
    set({ mode: m });
  },
  toggle: () => {
    const next: Mode = get().mode === 'day' ? 'night' : 'day';
    get().setMode(next);
  },
}));

/** Sync the body class to the persisted mode on first paint. */
export function bootstrapTheme() {
  const m = readInitial();
  document.body.classList.toggle('dark', m === 'night');
}
